/**
 * CommandStack - Manages undo/redo history
 *
 * Maintains two stacks:
 * - undoStack: Commands that can be undone
 * - redoStack: Commands that can be redone
 *
 * @example
 * const stack = new CommandStack(50);
 * stack.execute(new CreateRectangleCommand(canvas, rect));
 * stack.undo(); // Remove rectangle
 * stack.redo(); // Add rectangle back
 */
export class CommandStack {
  /**
   * @param {number} maxHistory - Maximum number of commands to keep (default: 50)
   */
  constructor(maxHistory = 50) {
    this.undoStack = [];
    this.redoStack = [];
    this.maxHistory = maxHistory;
  }

  /**
   * Execute a command and add it to the undo stack
   * @param {Command} command - Command to execute
   */
  execute(command) {
    if (!command) {
      console.warn('CommandStack.execute: command is null/undefined');
      return;
    }

    try {
      command.execute();
      this.undoStack.push(command);
      this.redoStack = []; // Clear redo stack on new action
      this._trimHistory();
    } catch (error) {
      console.error('Error executing command:', error);
      throw error;
    }
  }

  /**
   * Undo the last command
   * @returns {boolean} True if undo was successful, false if stack is empty
   */
  undo() {
    if (!this.canUndo()) {
      return false;
    }

    const command = this.undoStack.pop();
    try {
      command.undo();
      this.redoStack.push(command);
      return true;
    } catch (error) {
      console.error('Error undoing command:', error);
      // Re-add to undo stack on error
      this.undoStack.push(command);
      throw error;
    }
  }

  /**
   * Redo the last undone command
   * @returns {boolean} True if redo was successful, false if stack is empty
   */
  redo() {
    if (!this.canRedo()) {
      return false;
    }

    const command = this.redoStack.pop();
    try {
      command.redo();
      this.undoStack.push(command);
      this._trimHistory();
      return true;
    } catch (error) {
      console.error('Error redoing command:', error);
      // Re-add to redo stack on error
      this.redoStack.push(command);
      throw error;
    }
  }

  /**
   * Check if undo is available
   * @returns {boolean} True if undo stack is not empty
   */
  canUndo() {
    return this.undoStack.length > 0;
  }

  /**
   * Check if redo is available
   * @returns {boolean} True if redo stack is not empty
   */
  canRedo() {
    return this.redoStack.length > 0;
  }

  /**
   * Clear all history (undo and redo stacks)
   */
  clear() {
    this.undoStack = [];
    this.redoStack = [];
  }

  /**
   * Get the number of commands in undo stack
   * @returns {number} Undo stack size
   */
  getUndoCount() {
    return this.undoStack.length;
  }

  /**
   * Get the number of commands in redo stack
   * @returns {number} Redo stack size
   */
  getRedoCount() {
    return this.redoStack.length;
  }

  /**
   * Get description of the next command that would be undone
   * @returns {string|null} Description or null if undo stack is empty
   */
  getUndoDescription() {
    if (!this.canUndo()) {
      return null;
    }
    const command = this.undoStack[this.undoStack.length - 1];
    return command.getDescription();
  }

  /**
   * Get description of the next command that would be redone
   * @returns {string|null} Description or null if redo stack is empty
   */
  getRedoDescription() {
    if (!this.canRedo()) {
      return null;
    }
    const command = this.redoStack[this.redoStack.length - 1];
    return command.getDescription();
  }

  /**
   * Trim undo stack to maxHistory size (remove oldest commands)
   * @private
   */
  _trimHistory() {
    if (this.undoStack.length > this.maxHistory) {
      const excess = this.undoStack.length - this.maxHistory;
      this.undoStack.splice(0, excess);
    }
  }
}
